import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { firmInfo } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Updates', path: '/updates' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-emerald-800 text-white py-2 hidden md:block">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href={`mailto:${firmInfo.email}`} className="flex items-center gap-2 hover:text-emerald-200 transition-colors">
              <Mail size={14} />
              {firmInfo.email}
            </a>
            <a href={`tel:${firmInfo.phone1}`} className="flex items-center gap-2 hover:text-emerald-200 transition-colors">
              <Phone size={14} />
              +91-{firmInfo.phone1}
            </a>
          </div>
          <div className="text-emerald-200">
            Chartered Accountants | Registered with ICAI
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_accounting-pros-7/artifacts/qi69ddhg_New_CA_India_Logo.png" 
                alt="CA India Logo"
                className="h-12 w-auto object-contain"
              />
              <div className="hidden sm:block">
                <h1 className="font-bold text-lg text-gray-900 leading-tight">{firmInfo.name}</h1>
                <p className="text-xs text-emerald-700">Chartered Accountants</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    isActive(link.path)
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-emerald-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <a href={`tel:${firmInfo.phone1}`}>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-lg hover:shadow-xl transition-all">
                  <Phone size={16} className="mr-2" />
                  Call Now
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t shadow-lg">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg font-medium transition-all ${
                    isActive(link.path)
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a href={`tel:${firmInfo.phone1}`} className="mt-2">
                <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
                  <Phone size={16} className="mr-2" />
                  Call Now
                </Button>
              </a>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
